<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  assistant: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const alert = useAlert();
const route = useRoute();
const router = useRouter();

const isSaving = ref(false);
const isDirty = ref(false);
const isSyncingFromStore = ref(false);
const lastSavedSnapshot = ref('');
const nickname = ref('');
const age = ref('');
const gender = ref('female');
const responseStyle = ref('adaptive');
const country = ref('Malaysia');
const preferredLanguage = ref('English');
const malayReplyForm = ref('auto');
const thaiReplyForm = ref('auto');
const msShortFormGlossary = ref([]);
const thChatGlossary = ref([]);
const selectedVoice = ref('');
const voiceSearch = ref('');

const assistantId = computed(() => props.assistant?.id);
const currentAssistant = computed(
  () =>
    store.getters['ailabAssistants/getRecord']?.(Number(assistantId.value)) ||
    props.assistant ||
    {}
);
const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig']);

const genderOptions = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'neutral', label: 'Neutral' },
];

const responseStyleOptions = [
  { value: 'adaptive', label: 'Adaptive' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
];

const countryOptions = [
  { value: 'Malaysia', label: 'Malaysia' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Thailand', label: 'Thailand' },
  { value: 'Vietnam', label: 'Vietnam' },
  { value: 'Philippines', label: 'Philippines' },
];

const languageOptions = [
  { value: 'English', label: 'English' },
  { value: 'Thai', label: 'Thai' },
  { value: 'Malay', label: 'Malay' },
  { value: 'Chinese', label: 'Chinese' },
  { value: 'Indonesian', label: 'Indonesian' },
  { value: 'Vietnamese', label: 'Vietnamese' },
  { value: 'Tamil', label: 'Tamil' },
];

const localLanguageHelp = computed(() => {
  switch ((country.value || '').toString().trim()) {
    case 'Malaysia':
      return 'Common coverage: Malay, English, Chinese, Tamil';
    case 'Thailand':
      return 'Common coverage: Thai, English';
    case 'Indonesia':
      return 'Common coverage: Indonesian, English';
    case 'Vietnam':
      return 'Common coverage: Vietnamese, English';
    case 'Singapore':
      return 'Common coverage: English, Chinese, Malay, Tamil';
    case 'Philippines':
      return 'Common coverage: English, Tagalog';
    default:
      return '';
  }
});

const voices = [
  { id: 'alloy', name: 'Alloy', description: 'Neutral and balanced' },
  { id: 'echo', name: 'Echo', description: 'Warm and engaging' },
  { id: 'fable', name: 'Fable', description: 'Expressive and dynamic' },
  { id: 'onyx', name: 'Onyx', description: 'Deep and authoritative' },
  { id: 'nova', name: 'Nova', description: 'Bright and energetic' },
  { id: 'shimmer', name: 'Shimmer', description: 'Soft and gentle' },
];

const filteredVoices = computed(() => {
  const query = (voiceSearch.value || '').toString().trim().toLowerCase();
  if (!query) return voices;
  return voices.filter(voice =>
    `${voice.name} ${voice.description} ${voice.id}`
      .toLowerCase()
      .includes(query)
  );
});

const currentResponseStyleLabel = computed(
  () =>
    responseStyleOptions.find(option => option.value === responseStyle.value)?.label ||
    'Adaptive'
);

const malayGlossaryCount = computed(
  () => normalizeGlossaryRows(msShortFormGlossary.value).length
);

const thaiGlossaryCount = computed(
  () => normalizeGlossaryRows(thChatGlossary.value).length
);

const safeToString = value => {
  if (value === null || value === undefined) return '';
  try {
    return String(value);
  } catch {
    return '';
  }
};

const normalizeGlossaryRows = rows => {
  const list = Array.isArray(rows) ? rows : [];
  const cleaned = list
    .map(row => ({
      short: (row?.short || '').toString().trim(),
      meaning: (row?.meaning || '').toString().trim(),
      note: (row?.note || '').toString().trim(),
    }))
    .filter(row => row.short && row.meaning);
  const seen = new Set();
  return cleaned.filter(row => {
    const key = row.short.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const settingsSnapshot = () =>
  JSON.stringify({
    nickname: safeToString(nickname.value),
    age: safeToString(age.value),
    gender: safeToString(gender.value || 'female'),
    country: safeToString(country.value || 'Malaysia'),
    preferred_language: safeToString(preferredLanguage.value || 'English'),
    ai_voice: safeToString(selectedVoice.value),
  });

const markSavedSnapshot = () => {
  lastSavedSnapshot.value = settingsSnapshot();
  isDirty.value = false;
};

const canSave = computed(
  () =>
    Boolean(
      assistantId.value &&
        !isSaving.value &&
        isDirty.value
    )
);

const openGuidanceTab = () => {
  router.replace({ query: { ...(route.query || {}), tab: 'train_guidance' } });
};

const syncFromAiCloser = () => {
  const cfg = aiCloserConfig.value || {};
  isSyncingFromStore.value = true;
  selectedVoice.value = safeToString(cfg.ai_voice);
  nickname.value = cfg.nickname || '';
  age.value = cfg.age || '';
  gender.value = cfg.gender || 'female';
  responseStyle.value = cfg.response_style || 'adaptive';
  country.value = cfg.country || 'Malaysia';
  preferredLanguage.value = cfg.preferred_language || 'English';
  const promptSections = cfg.prompt_sections || {};
  const languageStyle = promptSections.language_style || {};
  malayReplyForm.value = languageStyle.ms_reply_form || 'auto';
  thaiReplyForm.value = languageStyle.th_reply_form || 'auto';
  msShortFormGlossary.value = normalizeGlossaryRows(
    languageStyle.ms_short_form_glossary
  );
  thChatGlossary.value = normalizeGlossaryRows(
    languageStyle.th_chat_glossary
  );
  markSavedSnapshot();
  isSyncingFromStore.value = false;
};

const showPreviewNotAvailable = () => {
  alert('Voice preview is not available yet');
};

const saveSettings = async () => {
  if (!assistantId.value) return;
  isSaving.value = true;
  try {
    const currentCfg = currentAssistant.value?.config || {};
    const nextCfg = {
      ...currentCfg,
      ai_voice: selectedVoice.value,
    };
    await store.dispatch('ailabAssistants/update', {
      id: assistantId.value,
      assistant: { config: nextCfg },
    });
    await store.dispatch('aiCloser/updateConfig', {
      assistantId: assistantId.value,
      data: {
        nickname: nickname.value || '',
        age: age.value ? parseInt(age.value, 10) : null,
        gender: gender.value || 'female',
        country: country.value || 'Malaysia',
        preferred_language: preferredLanguage.value || 'English',
        ai_voice: selectedVoice.value || null,
      },
    });
    await store.dispatch('ailabAssistants/show', assistantId.value);
    await store.dispatch('aiCloser/fetchConfig', { assistantId: assistantId.value });
    syncFromAiCloser();
    alert('Settings updated');
  } catch (error) {
    alert(error?.message || 'Failed to update settings');
  } finally {
    isSaving.value = false;
  }
};

defineExpose({
  save: saveSettings,
  canSave: () => canSave.value,
});

watch(
  () => aiCloserConfig.value,
  () => {
    if (isDirty.value || isSaving.value) return;
    syncFromAiCloser();
  },
  { immediate: true, deep: true }
);

watch(
  [
    nickname,
    age,
    gender,
    country,
    preferredLanguage,
    selectedVoice,
  ],
  () => {
    if (isSyncingFromStore.value) return;
    if (!lastSavedSnapshot.value) return;
    isDirty.value = settingsSnapshot() !== lastSavedSnapshot.value;
  },
  { deep: true }
);

onMounted(async () => {
  if (assistantId.value) {
    await store.dispatch('aiCloser/fetchConfig', { assistantId: assistantId.value });
  }
});
</script>

<template>
  <div class="fin-ai flex flex-col h-full min-h-0">
    <div class="fin-page-header-row settings-header">
      <div>
        <h1 class="heading o__size-1">Settings</h1>
        <p class="settings-note mt-1">
          Manage runtime tuning and compatibility flags without mixing them back into Get Started or Deploy.
        </p>
      </div>
    </div>

    <div class="fin-page-body settings-shell">
      <div class="settings-grid">
        <div class="settings-card">
          <div class="settings-card-title">AI identity & voice</div>
          <div class="settings-note">
            These values stay in `ai_closer_config` and remain the runtime owner for the
            identity section used by prompt construction, plus the configured AI voice.
          </div>

          <div class="settings-field-grid">
            <label class="settings-field">
              <span class="settings-label">Nickname</span>
              <input v-model="nickname" type="text" class="settings-input" maxlength="30" />
            </label>
            <label class="settings-field">
              <span class="settings-label">Age</span>
              <input v-model="age" type="text" class="settings-input" />
            </label>
            <label class="settings-field">
              <span class="settings-label">Gender</span>
              <select v-model="gender" class="settings-input">
                <option v-for="option in genderOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>

          <div class="settings-routing-card">
            <div>
              <div class="settings-label">Communication style moved</div>
              <div class="settings-note">
                `response_style` and localized runtime language-style rules now belong to
                `Train > Guidance` so Settings no longer writes those prompting-behavior
                fields directly.
              </div>
              <div class="settings-owner-summary">
                Current style: {{ currentResponseStyleLabel }}
              </div>
            </div>
            <button type="button" class="btn o__secondary" @click="openGuidanceTab">
              Open Guidance
            </button>
          </div>

          <div class="settings-field">
            <span class="settings-label">AI voice</span>
            <span class="settings-note">
              Stored in `ai_closer_config.ai_voice`. Preview is not available yet.
            </span>
            <div class="settings-voice-search">
              <span class="i-lucide-search size-4 text-slate-400" />
              <input
                v-model="voiceSearch"
                type="text"
                class="settings-voice-search-input"
                placeholder="Search voices"
              />
            </div>
            <div class="settings-voice-list">
              <div
                v-for="voice in filteredVoices"
                :key="voice.id"
                class="settings-voice-item"
                :class="{ selected: selectedVoice === voice.id }"
                @click="selectedVoice = voice.id"
              >
                <div class="settings-voice-radio">
                  <span
                    v-if="selectedVoice === voice.id"
                    class="settings-voice-checked"
                  />
                </div>
                <div class="settings-voice-info">
                  <div class="settings-label">{{ voice.name }}</div>
                  <div class="settings-note">{{ voice.description }}</div>
                </div>
                <button
                  type="button"
                  class="btn o__secondary settings-voice-preview"
                  @click.stop="showPreviewNotAvailable"
                >
                  Preview
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="settings-card">
          <div class="settings-card-title">Locale & language style</div>
          <div class="settings-note">
            `country` and `preferred_language` still live here as identity/locale inputs for
            runtime prompt construction. Runtime tone/style rules now live in `Train > Guidance`.
          </div>

          <div class="settings-field-grid">
            <label class="settings-field">
              <span class="settings-label">Country</span>
              <select v-model="country" class="settings-input">
                <option v-for="option in countryOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </label>
            <label class="settings-field">
              <span class="settings-label">Preferred language</span>
              <select v-model="preferredLanguage" class="settings-input">
                <option
                  v-for="option in languageOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <span v-if="localLanguageHelp" class="settings-note">{{ localLanguageHelp }}</span>
            </label>
          </div>

          <div class="settings-routing-card">
            <div>
              <div class="settings-label">Guidance-owned language style</div>
              <div class="settings-note">
                Malay reply form: <strong>{{ malayReplyForm || 'auto' }}</strong>,
                Thai reply form: <strong>{{ thaiReplyForm || 'auto' }}</strong>,
                Malay glossary rows: <strong>{{ malayGlossaryCount }}</strong>,
                Thai glossary rows: <strong>{{ thaiGlossaryCount }}</strong>.
              </div>
            </div>
            <button type="button" class="btn o__secondary" @click="openGuidanceTab">
              Edit in Guidance
            </button>
          </div>
        </div>
      </div>

      <div class="settings-actions settings-actions--footer">
        <button type="button" class="btn o__secondary" :disabled="!canSave" @click="saveSettings">
          {{ isSaving ? 'Saving...' : 'Save settings' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings-shell { display: flex; flex-direction: column; gap: 16px; min-height: 0; }
.settings-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
.settings-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.settings-card { border: 1px solid var(--fin-neutral-border); background: #fff; border-radius: 14px; padding: 16px; }
.settings-routing-card { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-top: 12px; padding: 12px; border: 1px dashed #cbd5e1; border-radius: 12px; background: #f8fafc; }
.settings-owner-summary { margin-top: 8px; font-size: 12px; line-height: 17px; color: #334155; }
.settings-card-title { font-size: 14px; line-height: 19px; font-weight: 650; color: #111827; }
.settings-label { font-size: 12px; line-height: 17px; font-weight: 600; color: #111827; }
.settings-note { font-size: 12px; line-height: 17px; color: #6b7280; }
.settings-field { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
.settings-field-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 12px; }
.settings-subgrid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 12px; }
.settings-input { width: 100%; border: 1px solid #d1d5db; border-radius: 10px; padding: 9px 11px; font-size: 13px; line-height: 18px; color: #111827; background: #fff; }
.settings-textarea { min-height: 92px; resize: vertical; }
.settings-json-textarea { min-height: 220px; font-family: ui-monospace, Menlo, Monaco, Consolas, monospace; font-size: 12px; line-height: 1.45; }
.settings-inline-code { font-size: 12px; padding: 2px 6px; border-radius: 6px; background: #f3f4f6; color: #374151; }
.settings-checkbox-row { display: flex; align-items: flex-start; gap: 12px; margin-top: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 12px; }
.settings-checkbox-row input { margin-top: 3px; }
.settings-range-row { display: flex; align-items: center; gap: 12px; margin-top: 2px; }
.settings-range { width: 100%; }
.settings-range-value { min-width: 28px; text-align: right; font-size: 12px; line-height: 17px; color: #6b7280; }
.settings-toggle-list { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.settings-toggle-row { display: flex; align-items: flex-start; gap: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 12px; }
.settings-toggle-row input { margin-top: 3px; }
.settings-glossary-list { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.settings-glossary-row { display: grid; grid-template-columns: 1.2fr 1.4fr 1.4fr auto; gap: 10px; align-items: center; }
.settings-bank-list { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.settings-bank-card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
.settings-bank-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.settings-voice-search { display: flex; align-items: center; gap: 10px; padding: 9px 11px; border: 1px solid #d1d5db; border-radius: 10px; background: #fff; margin-top: 8px; }
.settings-voice-search-input { flex: 1; border: none; background: transparent; font-size: 13px; line-height: 18px; color: #111827; }
.settings-voice-search-input:focus { outline: none; }
.settings-voice-list { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; max-height: 280px; overflow-y: auto; }
.settings-voice-item { display: flex; align-items: center; gap: 12px; padding: 11px 12px; border: 1px solid #e5e7eb; border-radius: 12px; cursor: pointer; background: #fff; }
.settings-voice-item.selected { border-color: #60a5fa; box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.12); }
.settings-voice-radio { width: 18px; height: 18px; border-radius: 9999px; border: 1.5px solid #94a3b8; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.settings-voice-checked { width: 8px; height: 8px; border-radius: 9999px; background: #2563eb; display: block; }
.settings-voice-info { flex: 1; min-width: 0; }
.settings-voice-preview { flex-shrink: 0; }
.settings-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 12px; }
.settings-actions--between { justify-content: space-between; }
.settings-actions--footer { justify-content: flex-end; }
@media (max-width: 1100px) {
  .settings-grid,
  .settings-field-grid,
  .settings-subgrid,
  .settings-glossary-row,
  .settings-bank-grid {
    grid-template-columns: 1fr;
  }
}
</style>
